import forIn from 'lodash/forIn';
import { UnityContext } from 'react-unity-webgl';
import {
  UNITY_CONTEXT_INITIALIZED,
  UNITY_SET_INITIALIZED,
  UNITY_ADD_COMMAND,
  UNITY_SET_SCENE,
} from '../constants/unity';
import { SIMULATION_RESET } from '../constants/simulation';
import { SIMULATION_START_SUCCESS } from '../constants/simulation';
import { GDL_INIT_SUCCESS } from '../constants/app';
import { ENG_INIT, ENG_SEND_WORK_AREA_SUCCESS } from '../constants/engineering';
import {
  FM_SAVE_REVISION_SUCCESS,
  FM_OPEN_REVISION_SUCCESS,
} from '../constants/filemanager';
import { updateSketcher, clearGraphics } from '../actions/engineering';
import { finishSimulation } from '../actions/simulation';
import { setLoading, setInitialized } from '../actions/unity';
import { markExerciseComplete } from '../actions/grades';

const queue = [];
const sendCommand = (command = undefined, unityInitialized = true) => {
  if (window._unityContext && unityInitialized) {
    // send any previously queued commands
    while (queue.length > 0) {
      let params = queue.shift();
      window._unityContext.send(...params);
    }
    // send this command, if supplied (this function may be used to send the queue)
    if (command) {
      window._unityContext.send(...command);
    }
  } else if (command !== undefined) {
    // queue commands until the player is ready
    queue.push(command);
  }
};

const initializeUnityContext = (webglFile, dispatch) => {
  let loaderUrl, dataUrl, frameworkUrl, codeUrl;
  // Get the file paths
  const root = webglFile.substring(0, webglFile.lastIndexOf('/'));
  const buildName = root.substring(root.lastIndexOf('/') + 1);

  const isSafari =
    /constructor/i.test(window.HTMLElement) ||
    (function (p) {
      return p.toString() === '[object SafariRemoteNotification]';
    })(
      !window['safari'] ||
        (typeof safari !== 'undefined' && safari.pushNotification)
    );

  if (isSafari) {
    console.log('Loading Unity with decompression fallback');
    loaderUrl = `${root}/Build/fallback/${buildName}.loader.js`;
    dataUrl = `${root}/Build/fallback/${buildName}.data.unityweb`;
    frameworkUrl = `${root}/Build/fallback/${buildName}.framework.js.unityweb`;
    codeUrl = `${root}/Build/fallback/${buildName}.wasm.unityweb`;
  } else {
    loaderUrl = `${root}/Build/${buildName}.loader.js`;
    dataUrl = `${root}/Build/${buildName}.data.gz`;
    frameworkUrl = `${root}/Build/${buildName}.framework.js.gz`;
    codeUrl = `${root}/Build/${buildName}.wasm.gz`;
  }

  // Initialize the unity context object
  const unityContext = new UnityContext({
    loaderUrl,
    dataUrl,
    frameworkUrl,
    codeUrl,
  });

  // Register event listeners
  unityContext.on('UpdateSketcher', (params) => {
    dispatch(updateSketcher(params));
  });
  unityContext.on('HideAll', () => {
    dispatch(clearGraphics());
  });
  unityContext.on('FinishSimulation', () => {
    dispatch(finishSimulation());
  });
  unityContext.on('SetWebPage', () => {
    dispatch(setInitialized());
  });
  unityContext.on('MarkInteractiveComplete', () => {
    dispatch(markExerciseComplete());
  });

  // Return the new UnityContext instance
  return unityContext;
};

const unityCommander = ({ dispatch, getState }) => (next) => (action) => {
  const {
    unity: { initialized, unityContextInitialized, webglPath },
  } = getState();

  if (action.type === GDL_INIT_SUCCESS) {
    // Initialize Unity/WebGL context (if needed)
    if (!unityContextInitialized && webglPath) {
      dispatch(setLoading(true));

      const context = initializeUnityContext(webglPath, dispatch);
      window._unityContext = context;

      dispatch({
        type: UNITY_CONTEXT_INITIALIZED,
        unityContextInitialized: true,
      });

      context.on('loaded', () => {
        dispatch(setLoading(false));
      });
    }
  } else if (action.type === UNITY_SET_INITIALIZED) {
    sendCommand(); // Will send any previously queued commands
  } else if (action.type === UNITY_SET_SCENE) {
    sendCommand(['sceneController', 'SetLevel', action.scene], initialized);
  } else if (
    //    action.type === FM_SAVE_REVISION_SUCCESS ||
    action.type === FM_OPEN_REVISION_SUCCESS
  ) {
    sendCommand(['sceneController', 'SetLevel', 0]);
  } else if (action.type === SIMULATION_RESET) {
    sendCommand(['sceneController', 'SetLevel', 1]);
  } else if (action.type === UNITY_ADD_COMMAND) {
    sendCommand(action.command);
  } else if (action.type === ENG_INIT) {
    forIn(action.data.graphics, (graphics) => {
      if (typeof graphics.unity === 'object') {
        if (typeof graphics.unity[0] === 'string') {
          // graphics.unity is a single graphics command
          sendCommand(graphics.unity);
        } else {
          // graphics.unity is an array of graphics commands
          graphics.unity.forEach((command) => sendCommand(command));
        }
      }
    });
  } else if (action.type === ENG_SEND_WORK_AREA_SUCCESS) {
    forIn(action.result.nodes, (node) => {
      forIn(node.unity, (command) => {
        sendCommand(command);
      });
    });
  } else if (action.type === SIMULATION_START_SUCCESS && action.result.unity) {
    if (!action.result.skipUnity) {
      forIn(action.result.unity, (command) => {
        sendCommand(command);
      });
    } 
  }

  return next(action);
};

export default unityCommander;
